<template>
  <v-card
    class="left-vertical-card d-flex flex-column"
    height="100%"
  >
    <div class="d-flex bg-titre px-4">
      <div class="secondary--text align-self-center pl-3 titre-h3 mr-auto">
        {{ titreModal }}
      </div>
      <v-btn
        icon
        class="align-self-center"
        @click="onClose"
      >
        <v-icon color="secondary">
          mdi-close
        </v-icon>
      </v-btn>
    </div>

    <v-card-text class="flex-grow-1 overflow-y-auto mt-3">
      <div
        v-if="errorMessage"
        class="mb-4"
      >
        <BaseMessage
          type="error"
          color="error"
          :message="errorMessage"
        />
      </div>

      <div 
        v-if="synthesesModifiable.length === 0 && !openFormNewSynthese && !loading.synthese" 
        class="d-flex justify-center my-8"
      >
        <BaseMessage
          type="info"
          color="secondary"
          text
          message="Aucun élément dans cette synthèse. Cliquez sur le bouton pour ajouter votre premier élément."
        />
      </div>

      <draggable
        v-if="!loading.synthese"
        v-model="synthesesModifiable"
        :disabled="!enabled || loading.update"
        handle=".handle"
        ghost-class="ghost"
        drag-class="dragging-item"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <div
          v-for="(syntheseItem, index) in synthesesModifiable"
          :key="syntheseItem.id || `new-index-${index}`"
        >
          <SaisieSynthese
            :contenu="syntheseItem"
            @openForm="handleOpenForm"
            @closeForm="handleCloseForm"
            @save="handleSave"
            @delete="handleDelete"
          />
        </div>
      </draggable>

      <div
        v-if="openFormNewSynthese"
        class="mt-4"
      >
        <SaisieSynthese
          :contenu="newSynthese"
          @closeForm="openFormNewSynthese = false"
          @save="handleSaveNew"
        />
      </div>
    </v-card-text>
    <BaseLoader v-if="loading.synthese || loading.update" />
    <v-card-actions class="pa-4">
      <v-row
        no-gutters
        justify="center"
      >
        <v-col cols="12">
          <AgrButton
            block
            color="primary"
            :disabled="openFormNewSynthese || loading.update || loading.synthese"
            @click="addSynthese"
          >
            <v-icon class="mr-2">
              mdi-plus
            </v-icon>Ajouter un élément
          </AgrButton>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { AgrButton } from '@agrial/ui'
import SaisieSynthese from './saisie-synthese.vue'
import BaseLoader from '@/components/common/base-loader.vue'
import BaseMessage from '@/components/common/base-message.vue'
import draggable from 'vuedraggable'
import { cloneDeep } from 'lodash'

export default {
  name: 'SaisieSyntheseModal',
  components: { AgrButton, BaseLoader, BaseMessage, SaisieSynthese, draggable },
  data () {
    return {
      synthesesModifiable: [], 
      loading: { synthese: false, update: false },
      enabled: true,
      dragging: false,
      newSynthese: {},
      openFormNewSynthese: false,
      errorMessage: null
    }
  },
  computed: {
    ...mapGetters({
      synthes_list: 'syntheseAnalyse/syntheseAnalyse'
    }),
    /**
     * Détermine dynamiquement le titre de la modale selon le nombre d'éléments.
     * @returns {string} Le titre textuel.
     */
    titreModal () {
      return this.synthesesModifiable.length > 0 ? 'Modifier la synthèse' : 'Rédiger la synthèse'
    }
  },
  watch: {
    synthes_list: {
      /**
       * Met à jour l'état de la liste locale lorsque la liste globale du store évolue.
       * @param {Object[]} newVal - Nouvelle liste issue du store global.
       */
      handler (newVal) {
        if (Array.isArray(newVal)) {
          this.synthesesModifiable = cloneDeep(newVal)
            .sort((a, b) => a.ordre - b.ordre)
            .map(s => ({ ...s, isOpenForm: false }))
        } else {
          this.synthesesModifiable = []
        }
      },
      immediate: true,
      deep: true
    }
  },
  /**
   * Hook de cycle de vie déclenché à la création du composant.
   * Permet de charger la liste initiale depuis le serveur via Vuex.
   */
  async created () {
    this.loading.synthese = true
    this.errorMessage = null
    try {
      await this.getSyntheseAnalyse()
    } catch (error) {
      this.errorMessage = 'Impossible de charger la liste des synthèses.'
    } finally {
      this.loading.synthese = false
    }
  },
  methods: {
    ...mapActions({
      getSyntheseAnalyse: 'syntheseAnalyse/getSyntheseAnalyse',
      createOrUpdateSynthese: 'syntheseAnalyse/createOrUpdateSyntheseAnalyse',
      saveOrdreSyntheseAnalyse: 'syntheseAnalyse/saveOrdreSyntheseAnalyse',
      deleteSyntheseAnalyse: 'syntheseAnalyse/deleteSyntheseAnalyse'
    }),

    /**
     * Intercepté en fin de Drag and Drop. Réindexe localement les ordres et les persiste en base de données.
     */
    onDragEnd () {
      this.dragging = false
      this.synthesesModifiable.forEach((item, index) => {
        item.ordre = index
      })
      this.savePositionsToServer()
    },

    /**
     * Ouvre l'éditeur de texte en ligne pour un élément spécifique et ferme les autres.
     * @param {number|string} id - Identifiant de la synthèse à modifier.
     */
    handleOpenForm (id) {
      this.errorMessage = null
      this.openFormNewSynthese = false
      this.synthesesModifiable = this.synthesesModifiable.map(s => ({
        ...s,
        isOpenForm: s.id === id
      }))
    },

    /**
     * Referme le formulaire d'édition actif pour une ligne.
     * @param {number|string} id - Identifiant de la synthèse concernée.
     */
    handleCloseForm (id) {
      this.synthesesModifiable = this.synthesesModifiable.map(s => 
        s.id === id ? { ...s, isOpenForm: false } : s
      )
    },

    /**
     * Enregistre les modifications textuelles apportées à une ligne existante.
     * @param {Object} updatedItem - La synthèse modifiée.
     */
    async handleSave (updatedItem) {
      this.loading.update = true
      this.errorMessage = null
      try {
        const itemCleaned = cloneDeep(updatedItem)
        delete itemCleaned.isOpenForm

        // Mise à jour unitaire en base (Ne gère que le message / le contenu)
        await this.createOrUpdateSynthese(itemCleaned)
        
        // Fermeture locale de la zone de saisie
        this.handleCloseForm(updatedItem.id)
      } catch (error) {
        this.errorMessage = 'Une erreur est survenue lors de la modification de la synthèse.'
      } finally {
        this.loading.update = false
      }
    },

    /**
     * Initialise l'état local pour accueillir l'affichage du nouveau formulaire vierge.
     */
    addSynthese () {
      this.errorMessage = null
      this.newSynthese = {
        message: '',
        ordre: this.synthesesModifiable.length,
        isOpenForm: true
      }
      this.openFormNewSynthese = true
    },

    /**
     * Enregistre sur le serveur une toute nouvelle ligne créée.
     * @param {Object} newCreated - Le payload de la nouvelle synthèse à créer.
     */
    async handleSaveNew (newCreated) {
      this.loading.update = true
      this.errorMessage = null
      try {
        const itemCleaned = cloneDeep(newCreated)
        delete itemCleaned.isOpenForm

        // Création unitaire sur le back-end
        await this.createOrUpdateSynthese(itemCleaned)
        
        // Fermeture du bloc formulaire de création global
        this.openFormNewSynthese = false
      } catch (error) {
        this.errorMessage = 'Une erreur est survenue lors de la création de la synthèse.'
      } finally {
        this.loading.update = false
      }
    },

    /**
     * Supprime un élément unitairement, réordonne le tableau restant et sauvegarde l'ordre final.
     * @param {number|string} id - Identifiant de l'élément à enlever.
     */
    async handleDelete (id) {
      this.loading.update = true
      this.enabled = false
      this.errorMessage = null

      const updatedList = this.synthesesModifiable.filter(s => s.id !== id)
      updatedList.forEach((s, idx) => {
        s.ordre = idx
      })

      try {
        if (id) {
          // 1. Suppression brute de la ligne cible
          await this.deleteSyntheseAnalyse({ id })
        }
        
        // 2. Sauvegarde de la réindexation ordonnée de la liste restante
        const payloadCleaned = cloneDeep(updatedList)
        payloadCleaned.forEach(s => delete s.isOpenForm)
        
        await this.saveOrdreSyntheseAnalyse(payloadCleaned)
      } catch (error) {
        this.errorMessage = 'Une erreur est survenue lors de la suppression de l\'élément.'
      } finally {
        this.enabled = true
        this.loading.update = false
      }
    },

    /**
     * Persiste l'état de tri (ordre) sur le back-end à l'aide de l'API globale d'ordonnancement.
     */
    async savePositionsToServer () {
      this.loading.update = true
      this.enabled = false
      this.errorMessage = null
      try {
        const payloadCleaned = cloneDeep(this.synthesesModifiable)
        payloadCleaned.forEach(s => delete s.isOpenForm)

        // Appel de l'API dédiée uniquement au tri
        await this.saveOrdreSyntheseAnalyse(payloadCleaned)
      } catch (error) {
        this.errorMessage = 'Une erreur est survenue lors du tri des éléments.'
      } finally {
        this.enabled = true
        this.loading.update = false
      }
    },

    /**
     * Émet l'événement de fermeture de la boîte modale au composant parent de l'application.
     */
    onClose () {
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.left-vertical-card {
  border-radius: 0 !important;
}
.bg-titre {
  height: 70px;
  background-color: #f1f2f5;
}
.ghost {
  opacity: 0.3;
  background: #c8ebfb;
}
::v-deep .dragging-item {
  cursor: grabbing !important;
  opacity: 0.9;
  box-shadow: 0 5px 15px rgba(0,0,0,0.15);
}
</style>